<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import type { ConnectedForm, FormInput } from '../types';
import type { TranslationKey } from '../i18n';
import InternationalPhoneInput from './InternationalPhoneInput.vue';
import { orderedCountries, stateOptions, type PhoneCountry } from '../formFields';

declare global {
	interface Window {
		intlTelInputGlobals?: {
			getCountryData: () => PhoneCountry[];
		};
	}
}

const props = defineProps<{
	form: ConnectedForm;
	modelValue: Record<string, unknown>;
	showErrors?: boolean;
	showHeading?: boolean;
	t: (key: TranslationKey) => string;
}>();

const emit = defineEmits<{
	'update:modelValue': [value: Record<string, unknown>];
}>();

const values = computed(() => props.modelValue);
const activeAgreement = ref<FormInput | null>(null);
const agreementDialog = ref<HTMLElement | null>(null);
const agreementCloseButton = ref<HTMLButtonElement | null>(null);
const agreementTrigger = ref<HTMLElement | null>(null);
const countryOptions = orderedCountries(window.intlTelInputGlobals?.getCountryData() ?? []);

const agreementTitle = computed(() => {
	if (!activeAgreement.value) return '';
	if (activeAgreement.value.type === 'terms') return props.t('termsConditions');
	return activeAgreement.value.title || props.t('consentDetails');
});

const agreementDetails = computed(() => {
	if (!activeAgreement.value) return '';
	return activeAgreement.value.type === 'terms' ? activeAgreement.value.label : activeAgreement.value.detail;
});

const openAgreement = (input: FormInput, event: MouseEvent): void => {
	agreementTrigger.value = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
	activeAgreement.value = input;
	void nextTick(() => agreementCloseButton.value?.focus());
};

const closeAgreement = (): void => {
	const trigger = agreementTrigger.value;
	activeAgreement.value = null;
	agreementTrigger.value = null;
	void nextTick(() => trigger?.focus());
};

const handleAgreementKeydown = (event: KeyboardEvent): void => {
	if (event.key === 'Escape') {
		event.preventDefault();
		closeAgreement();
		return;
	}
	if (event.key !== 'Tab' || !agreementDialog.value) return;
	const focusable = Array.from(agreementDialog.value.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
		.filter((element) => !element.hasAttribute('disabled'));
	if (!focusable.length) return;
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (event.shiftKey && document.activeElement === first) {
		event.preventDefault();
		last.focus();
	} else if (!event.shiftKey && document.activeElement === last) {
		event.preventDefault();
		first.focus();
	}
};

watch(activeAgreement, (agreement) => {
	document.body.classList.toggle('agreement-modal-open', Boolean(agreement));
});

onBeforeUnmount(() => document.body.classList.remove('agreement-modal-open'));

const keyFor = (input: FormInput): string => input.name || `field_${input.id}`;

const update = (input: FormInput, value: unknown): void => {
	emit('update:modelValue', {
		...props.modelValue,
		[keyFor(input)]: value,
	});
};

const toggleMultiple = (input: FormInput, option: string, checked: boolean): void => {
	const key = keyFor(input);
	const current = Array.isArray(props.modelValue[key]) ? props.modelValue[key] as string[] : [];
	update(input, checked ? [...new Set([...current, option])] : current.filter((value) => value !== option));
};

const starSelected = (input: FormInput, rating: number): boolean =>
	Number(values.value[keyFor(input)] ?? 0) >= rating;

const empty = (input: FormInput): boolean => {
	const value = values.value[keyFor(input)];
	if (input.type === 'multi-select' && input.minimumSelections > 0) {
		return !Array.isArray(value) || value.length < input.minimumSelections;
	}
	if (['checkbox', 'checkbox-optout', 'terms', 'consent'].includes(input.type)) return !Boolean(value);
	return value == null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);
};

const invalidEmail = (input: FormInput): boolean => {
	if (input.type !== 'email') return false;
	const value = String(values.value[keyFor(input)] ?? '').trim();
	return Boolean(value) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};
</script>

<template>
	<div class="dynamic-form">
		<header v-if="showHeading !== false && (form.title || form.description)" class="form-heading">
			<h2 v-if="form.title">{{ form.title }}</h2>
			<p v-if="form.description" class="muted">{{ form.description }}</p>
		</header>

		<div v-for="input in form.inputs" :key="input.id" class="field-group">
			<label v-if="!['checkbox', 'checkbox-optout', 'terms', 'consent'].includes(input.type)" class="field-label" :for="`dynamic-${input.id}`">
				{{ input.label }}
				<span v-if="input.isRequired" aria-hidden="true">*</span>
			</label>

			<textarea
				v-if="input.type === 'textarea'"
				:id="`dynamic-${input.id}`"
				:value="String(values[keyFor(input)] ?? '')"
				:required="input.isRequired"
				rows="4"
				@input="update(input, ($event.target as HTMLTextAreaElement).value)"
			/>

			<select
				v-else-if="input.type === 'state'"
				:id="`dynamic-${input.id}`"
				:value="String(values[keyFor(input)] ?? '')"
				:required="input.isRequired"
				@change="update(input, ($event.target as HTMLSelectElement).value)"
			>
				<option value="">{{ t('chooseOne') }}</option>
				<option v-for="option in stateOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
			</select>

			<select
				v-else-if="input.type === 'country'"
				:id="`dynamic-${input.id}`"
				:value="String(values[keyFor(input)] ?? '')"
				:required="input.isRequired"
				@change="update(input, ($event.target as HTMLSelectElement).value)"
			>
				<option value="">{{ t('chooseOne') }}</option>
				<option v-for="country in countryOptions" :key="country.iso2" :value="country.name">{{ country.name }}</option>
			</select>

			<select
				v-else-if="input.type === 'select'"
				:id="`dynamic-${input.id}`"
				:value="String(values[keyFor(input)] ?? '')"
				:required="input.isRequired"
				@change="update(input, ($event.target as HTMLSelectElement).value)"
			>
				<option value="">{{ t('chooseOne') }}</option>
				<option v-for="option in input.options" :key="option" :value="option">{{ option }}</option>
			</select>

			<div v-else-if="['single-select', 'likert'].includes(input.type)" class="check-list">
				<label v-for="option in input.options" :key="option" class="check-option">
					<input
						:name="`dynamic-${input.id}`"
						type="radio"
						:value="option"
						:checked="values[keyFor(input)] === option"
						@change="update(input, option)"
					>
					<span>{{ option }}</span>
				</label>
			</div>

			<div v-else-if="input.type === 'multi-select'" class="check-list">
				<label v-for="option in input.options" :key="option" class="check-option">
					<input
						type="checkbox"
						:checked="Array.isArray(values[keyFor(input)]) && (values[keyFor(input)] as string[]).includes(option)"
						@change="toggleMultiple(input, option, ($event.target as HTMLInputElement).checked)"
					>
					<span>{{ option }}</span>
				</label>
			</div>

			<div v-else-if="input.type === 'star'" class="star-options" role="radiogroup" :aria-label="input.label">
				<button
					v-for="rating in [1, 2, 3, 4, 5]"
					:key="rating"
					type="button"
					class="star-option"
					:class="{ selected: starSelected(input, rating) }"
					role="radio"
					:aria-checked="Number(values[keyFor(input)] ?? 0) === rating"
					:aria-label="`${rating} stars`"
					@click="update(input, rating)"
				>
					<span aria-hidden="true">★</span>
				</button>
			</div>

			<div v-else-if="['terms', 'consent'].includes(input.type)" class="check-option agreement-option">
				<input
					:id="`dynamic-${input.id}`"
					type="checkbox"
					:checked="Boolean(values[keyFor(input)])"
					@change="update(input, ($event.target as HTMLInputElement).checked)"
				>
				<span class="agreement-copy">
					<label :for="`dynamic-${input.id}`">{{ input.type === 'terms' ? t('iAgree') : input.label }}</label>
					{{ ' ' }}
					<button type="button" class="agreement-link" @click="openAgreement(input, $event)">
						{{ input.type === 'terms' ? t('termsConditions') : t('viewDetails') }}
					</button>
					<span v-if="input.isRequired" aria-hidden="true"> *</span>
				</span>
			</div>

			<label v-else-if="['checkbox', 'checkbox-optout'].includes(input.type)" class="check-option">
				<input
					:id="`dynamic-${input.id}`"
					type="checkbox"
					:checked="Boolean(values[keyFor(input)])"
					@change="update(input, ($event.target as HTMLInputElement).checked)"
				>
				<span>{{ input.title || input.label }} <span v-if="input.isRequired" aria-hidden="true">*</span></span>
			</label>

			<InternationalPhoneInput
				v-else-if="input.type === 'i11lphone'"
				:id="`dynamic-${input.id}`"
				:model-value="String(values[keyFor(input)] ?? '')"
				:required="input.isRequired"
				@update:model-value="update(input, $event)"
			/>

			<input
				v-else
				:id="`dynamic-${input.id}`"
				:type="input.type === 'email' ? 'email' : input.type === 'phone' ? 'tel' : input.type === 'number' ? 'number' : input.type === 'date' ? 'date' : 'text'"
				:value="String(values[keyFor(input)] ?? '')"
				:required="input.isRequired"
				@input="update(input, ($event.target as HTMLInputElement).value)"
			>

			<p v-if="input.detail && !['terms', 'consent'].includes(input.type)" class="field-detail muted">{{ input.detail }}</p>

			<p v-if="showErrors && input.isRequired && empty(input)" class="field-error">{{ t('required') }}</p>
			<p v-else-if="showErrors && invalidEmail(input)" class="field-error">{{ t('validEmail') }}</p>
		</div>

		<div v-if="activeAgreement" class="agreement-modal" @click.self="closeAgreement" @keydown="handleAgreementKeydown">
			<div class="agreement-modal-dialog">
				<article ref="agreementDialog" class="agreement-modal-content" role="dialog" aria-modal="true" aria-labelledby="agreement-title">
					<header class="agreement-modal-header">
						<h2 id="agreement-title">{{ agreementTitle }}</h2>
						<button ref="agreementCloseButton" type="button" class="agreement-modal-close" :aria-label="t('close')" @click="closeAgreement"><span aria-hidden="true">&times;</span></button>
					</header>
					<div class="agreement-modal-body"><div class="agreement-details" v-html="agreementDetails" /></div>
					<footer class="agreement-modal-footer">
						<button type="button" class="primary-action agreement-modal-button" @click="closeAgreement">{{ t('close') }}</button>
					</footer>
				</article>
			</div>
		</div>
	</div>
</template>
